/**
 * Composer 시뮬레이션 순수 로직 — SimulationPage의 상태 구성 규칙을 테스트 가능한 단위로 분리.
 *
 * ⚠️ 초기값·prefill listRows 규칙은 **web 배포 작성 페이지(apps/web/src/pages/reports/new-composed.tsx)와
 * 동형**이어야 한다(시뮬=배포판 충실도 제1원칙 — docs/decisions/2026-07-26-composer-simulation.md).
 * web 쪽 초기값/AI 후보/prefill 규칙이 바뀌면 여기도 같이 고칠 것. 수집기 자체
 * (collectWidgetDefaultValues 등)는 rc 정본을 그대로 소비하므로 규칙 드리프트 표면은
 * 이 파일의 "조립 순서"뿐이다.
 */
import {
  deriveWriteForm,
  collectSortableSelectOptionSources,
  collectVariableOptionsSourceKeys,
  collectWidgetDefaultValues,
} from '@safetyhub/report-composer';
import type { ApprovalBlockData } from '@safetyhub/report-composer';
import type {
  ApprovalLineDefinitionRow,
  InputWidget,
  ReportComposerPreviewContext,
  ReportTemplate,
} from '@safetyhub/shared';
import { buildComposerApprovalData } from '../../lib/composerApprovalData';

/**
 * 작성 폼 노드 — 파생 하나가 정본이다(web new-composed와 동일 규칙).
 *
 * ⚠️ 2026-09-30(S4): 종전에는 `template.writeForm?.length ? template.writeForm : derive(...)`
 * 삼항이었다. 그 앞 갈래는 저장된 override를 살리는 길이 아니라 **파생 골격을 버리는 길**이었다 —
 * 작성 폼 골격의 출처는 컬럼이 아니라 본문 바인딩이다. web 쪽도 같은 커밋 계열에서 같은 형태로
 * 바뀌었다 — 이 파일의 「시뮬=배포판 동형」 제1원칙이 그것을 요구한다.
 * 2026-10-02(S6): 그 저장 override 채널(`ReportTemplate.writeForm`) 자체가 철거됐으므로 이제
 * 갈래가 하나뿐이다 — 파생 + (소비처가 우선하는) 저작 트리.
 */
export function deriveFormNodes(template: ReportTemplate): InputWidget[] {
  return deriveWriteForm(template);
}

/**
 * 작성 폼 초기값 구성 — web new-composed 템플릿 로드 effect와 동형:
 * 1) 각 노드 defaultValue (prefill 없을 때의 fallback)
 * 2) widget:'table' 노드(=dataDef)는 dataDef.defaultRows deep clone으로 seed
 * 3) 트리(composerWidgetTree) 저작 defaultValue를 파생분 위에 덮음(같은 bindingKey면 트리가 정본)
 *
 * ⚠️ **이 로직은 «렌더 밖»에서 파생 노드를 먹는 네 자리 중 하나다** — 나머지 셋은 web
 * `new-composed.tsx`(생성 effect 안 인라인 루프) · web `external/composed/[token].tsx`
 * (`deriveInitialValues`) · admin `EditorPage.tsx`(`writeFormFields` prop). 「작성 폼 렌더는
 * 저작 트리가 정본」(`writeTree = 저작 트리 ?? 파생`)이라는 규약은 **렌더에만** 적용되므로,
 * 「파생이 렌더에 안 쓰인다」를 「파생이 안 쓰인다」로 읽지 말 것.
 *
 * ⭐ 그래서 2026-10-02(S6)에 flat override(`ReportTemplate.writeForm` + rc `applyOverride`)를
 * 철거할 때 **이 자리의 영향을 따로 쟀다.** `widget` 값에 따라 갈리는 분기는 **`'table'` 하나뿐**이고
 * (`defaultValue`는 override가 건드리지 않던 축이다), 영향은 **0**이다 — 근거 둘:
 *   1. **구조**: flat 노드의 유일한 생산자는 form-import이고 `widgetForFieldType`이 낼 수 있는 값은
 *      `textarea|date|number|checkbox|text` 닫힌 5개다 — **`table`이 그 안에 없다.** 따라서
 *      「flat이 비-table 노드를 table로 바꿔 seed를 **추가**」하는 방향은 원리적으로 불가.
 *   2. **실측**: 반대 방향(flat의 bindingKey가 `dataDef.key`와 같아 파생의 `'table'`을 덮어 seed를
 *      **억제**)은 dev flat 보유 6템플릿 **251노드 중 0건**(dataDef 보유 템플릿 1개 포함),
 *      flat `widget` distinct에도 `table` 0건. ⇒ 철거는 seed를 없애는 쪽이 아니라 (있었다면)
 *      되살리는 쪽이다.
 */
export function buildInitialWriteValues(
  template: ReportTemplate,
  nodes: InputWidget[],
): Record<string, unknown> {
  const initial: Record<string, unknown> = {};
  for (const node of nodes) {
    if (node.defaultValue !== undefined) {
      initial[node.bindingKey] = node.defaultValue;
    }
    if (node.widget === 'table') {
      const def = template.dataDefs?.find((d) => d.key === node.bindingKey);
      if (def) {
        initial[node.bindingKey] = structuredClone(def.defaultRows);
      }
    }
  }
  Object.assign(
    initial,
    collectWidgetDefaultValues(template.composerWidgetTree ?? [], template.dataDefs),
  );
  return initial;
}

/**
 * prefill 결과에서 카탈로그 list 후보 소스(rows 배열)만 추려 resolvedRows 채널 주입분을 만든다 —
 * web new-composed의 listRows 추출과 동형. values 채널만으론 **모달 안** sortableSelect/변수 select에
 * rows가 도달하지 못하므로(모달 스코프 격리) resolvedRows 채널이 필요하다.
 */
export function collectPrefillListRows(
  template: ReportTemplate,
  prefill: Record<string, unknown>,
): Record<string, Record<string, unknown>[]> {
  const sourceKeys = [
    ...new Set([
      ...collectSortableSelectOptionSources(template.composerWidgetTree ?? []),
      ...collectVariableOptionsSourceKeys(template.variables),
    ]),
  ];
  const listRows: Record<string, Record<string, unknown>[]> = {};
  for (const key of sourceKeys) {
    const value = prefill[key];
    if (Array.isArray(value)) listRows[key] = value as Record<string, unknown>[];
  }
  return listRows;
}

/** 제목 자동 생성 — web new-composed 제출 시 규칙과 동형(`${템플릿명} - ${현장명} (${날짜})`). */
export function buildSimulationTitle(
  templateName: string | undefined,
  siteName: string,
  now: Date = new Date(),
): string {
  return `${templateName || '보고서'} - ${siteName} (${now.toLocaleDateString('ko-KR')})`;
}

/**
 * dry-run 결과의 결재라인 정의 → 결재란(approvalBlock special) 표시 입력.
 * 실생성 직후 보고서는 DRAFT — 상세와 동일하게 status='DRAFT'로 빌드해 결재라인(정의)은 그리되
 * 서명은 숨긴다(buildComposerApprovalData의 shouldHideSignatures 규칙 그대로).
 */
export function buildSimulationApprovalData(
  approvalLineDefinition: ApprovalLineDefinitionRow[] | null,
): ApprovalBlockData {
  return buildComposerApprovalData({
    status: 'DRAFT',
    approvalLineDefinition,
    approvalSignatures: [],
    authorSignerName: null,
    authorSignatureData: null,
    approvalSignatureMode: null,
  });
}

/** 시뮬레이션에서 로컬 합성하는 카탈로그 등록 행 — 서버 write 없이(부수효과 0) 콤보 제안 경로만 재현. */
export function buildLocalCatalogRow(
  submitValues: Record<string, string>,
  id: string,
): Record<string, unknown> {
  return { ...submitValues, id };
}

// ── 마지막 사용 컨텍스트 영속(localStorage) — 새로고침/재시작 시 조직·현장·점검·계정 복원 ──

const SIM_CONTEXT_STORAGE_KEY = 'composer-sim-last-context';

/** 저장소에서 취하는 필드 화이트리스트 — 임의 키 오염 방어(전부 string/string[]만). */
function sanitizeSimContext(raw: unknown): ReportComposerPreviewContext {
  if (!raw || typeof raw !== 'object') return {};
  const o = raw as Record<string, unknown>;
  const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);
  const strArr = (v: unknown): string[] | undefined =>
    Array.isArray(v) && v.length > 0 && v.every((x) => typeof x === 'string')
      ? (v as string[])
      : undefined;
  const ctx: ReportComposerPreviewContext = {};
  const companyId = str(o.companyId);
  if (!companyId) return {}; // 조직 없이 하위만 남으면 캐스케이드가 무의미 — 통째 초기화
  ctx.companyId = companyId;
  const siteId = str(o.siteId);
  if (siteId) ctx.siteId = siteId;
  const inspectionIds = strArr(o.inspectionIds);
  if (inspectionIds) {
    ctx.inspectionIds = inspectionIds;
    ctx.inspectionId = inspectionIds[0];
  } else {
    const inspectionId = str(o.inspectionId);
    if (inspectionId) ctx.inspectionId = inspectionId;
  }
  const userId = str(o.userId);
  if (userId) ctx.userId = userId;
  // ⚠️ 모사 값(previewSimulation)은 **일부러 빠진다** — 화이트리스트라 저절로 그렇게 되지만,
  //    그게 규약이다(결정 2026-09-01 §③·⑥): 계획 산출 값은 다음 세션 데이터에 없을 수 있고,
  //    스칼라 모사도 조용히 되살아나면 저작자가 실데이터로 착각한다.
  //    ⚠️ **EditorPage와는 이제 갈린다** — 에디터는 2026-09-03부터 모사를 영속하고(상시 배너 +
  //    stale 축 해제로 두 위험을 각각 막는다), 이 페이지엔 그 두 장치가 없어 종전대로 버린다.
  return ctx;
}

/** 마지막 사용 컨텍스트 복원 — 파싱 실패/비어있음이면 빈 컨텍스트. */
export function readLastSimContext(
  storage: Pick<Storage, 'getItem'> | undefined = typeof localStorage === 'undefined'
    ? undefined
    : localStorage,
): ReportComposerPreviewContext {
  try {
    const raw = storage?.getItem(SIM_CONTEXT_STORAGE_KEY);
    if (!raw) return {};
    return sanitizeSimContext(JSON.parse(raw));
  } catch {
    return {};
  }
}

/** 컨텍스트 변경 즉시 저장 — "작성 시작" 전 새로고침에도 마지막 입력이 남게. 저장 실패는 무해 무시. */
export function writeLastSimContext(
  context: ReportComposerPreviewContext,
  storage: Pick<Storage, 'setItem'> | undefined = typeof localStorage === 'undefined'
    ? undefined
    : localStorage,
): void {
  try {
    storage?.setItem(SIM_CONTEXT_STORAGE_KEY, JSON.stringify(sanitizeSimContext(context)));
  } catch {
    // storage 불가 환경(사파리 프라이빗 등) — 복원 기능만 조용히 비활성
  }
}
